'use client'

import { FC, ChangeEvent } from 'react'
import { testIds } from '@/root/shared-consts'
import { FieldErrors, FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useGetTranslations } from '@/root/shared-hooks'
import { PersonFormFields, VALIDATION_MESSAGES, type FieldConfig } from '@/root/modules/shared/ConfirmPage'
import { GeneralInput } from './GeneralInput'
import { intlCodeValidationPattern } from './utils'
import cn from 'classnames'

const CUSTOMER_INPUT_TESTID: Record<string, string> = {
  firstName: testIds.confirm.customerInputFirstName,
  lastName: testIds.confirm.customerInputLastName,
  phoneNumber: testIds.confirm.customerInputPhoneNumber,
  email: testIds.confirm.customerInputEmail,
}

interface PersonFormProps {
  register: UseFormRegister<FieldValues>
  setValue: UseFormSetValue<FieldValues>
  errors: FieldErrors<FieldValues>
  clearErrors: (name?: string) => void
  fields: FieldConfig[]
  onBlur: () => void
  readOnly?: boolean
}

export const PersonForm: FC<PersonFormProps> = ({
  register,
  errors,
  clearErrors,
  fields,
  onBlur,
  setValue,
  readOnly,
}) => {
  const { getTranslation } = useGetTranslations()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, name, value } = event.target
    setValue(id, value)
    clearErrors(name)
  }

  const handleBlur = () => {
    onBlur()
  }

  const intlCodeError = Boolean(errors[PersonFormFields.INTL_CODE])

  const renderField = (field: FieldConfig) => {
    const hasError = Boolean(errors[field.id])
    const errorMessage =
      errors[field.id]?.message === VALIDATION_MESSAGES.INVALID_FORMAT
        ? field.type === 'email'
          ? getTranslation('confirmationContactFormInvalidEmailError')
          : getTranslation('confirmationContactFormInvalidFormatError')
        : getTranslation('confirmationContactFormPhoneRequiredError')

    // Phone field needs special treatment
    if (field.isPhone) {
      return (
        <div key={field.id} className="flex w-full flex-col gap-1.5">
          <label className="text-neutral-darker text-sm leading-none">
            {field.label} <span className="text-error">*</span>
          </label>
          <div className="flex w-full items-start gap-2.5">
            <div className="flex w-20 shrink-0 flex-col gap-1.5">
              <input
                className={cn(
                  'w-full rounded-lg border bg-white px-2.5 py-1.5 text-center text-base leading-none',
                  'focus:border-primary-base focus:outline-none',
                  intlCodeError ? 'border-error' : 'border-neutral-medium-lighter',
                  readOnly && 'bg-neutral-very-light cursor-not-allowed'
                )}
                readOnly={readOnly}
                data-testid={testIds.confirm.customerInputPhoneCode}
                placeholder={getTranslation('confirmationContactFormCodePlaceHolder')}
                {...register(PersonFormFields.INTL_CODE, {
                  required: true,
                  pattern: { value: intlCodeValidationPattern, message: VALIDATION_MESSAGES.INVALID_FORMAT },
                  onBlur: handleBlur,
                })}
              />
              {intlCodeError && (
                <span className="text-error text-xs">
                  {getTranslation('confirmationContactFormInvalidFormatError')}
                </span>
              )}
            </div>
            <GeneralInput
              id={field.id}
              testId={CUSTOMER_INPUT_TESTID[field.id]}
              placeholder={field.placeholder}
              onChange={handleChange}
              hasError={hasError}
              errorMessage={errorMessage}
              validationPattern={field.validationPattern!}
              register={register}
              onBlur={handleBlur}
              readOnly={readOnly}
              showRequired={false}
            />
          </div>
        </div>
      )
    }

    // Regular text/email field
    return (
      <GeneralInput
        key={field.id}
        label={field.label}
        id={field.id}
        testId={CUSTOMER_INPUT_TESTID[field.id]}
        type={field.type}
        placeholder={field.placeholder}
        onChange={handleChange}
        hasError={hasError}
        errorMessage={errorMessage}
        validationPattern={field.validationPattern!}
        register={register}
        onBlur={handleBlur}
        readOnly={readOnly}
        showRequired
      />
    )
  }

  return <form className="flex flex-col gap-5">{fields.map(renderField)}</form>
}

export default PersonForm
